/**
 * Document and uploader-group tiles shown inside an open folder.
 *
 * Moved out of FolderView.tsx unchanged (CR-7754) to keep it under the
 * 500-line cap.
 */

import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Folder,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  User,
  Building2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Document } from '../../lib/api';
import { getFileIcon } from './utils';
import { DocumentRowList } from './DocumentRows';

/** Tile vs list layout for the contents of an open folder. */
export type SubLayout = 'tiles' | 'list';


interface SupplierTileProps {
  label: string;
  count: number;
  onOpen: () => void;
}

export function SupplierTile({ label, count, onOpen }: SupplierTileProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={label}
      className="group flex flex-col rounded-lg border bg-card p-3 text-left transition-all duration-150 ease-out hover:border-primary/50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-center justify-between">
        <Folder className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.4} />
        <span className="text-[11px] font-semibold tabular-nums text-muted-foreground group-hover:text-foreground">
          {count}
        </span>
      </div>
      <p className="mt-3 text-[13px] font-medium leading-snug line-clamp-2 break-words">
        {label}
      </p>
    </button>
  );
}

interface SupplierRowProps {
  label: string;
  documents: Document[];
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  docLayout: SubLayout;
}

export function SupplierRow({ label, documents, onDownload, onDelete, onEdit, onPreview, onOpenDetail, docLayout }: SupplierRowProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{label}</span>
        </div>
        <Badge variant="secondary" className="shrink-0">{documents.length}</Badge>
      </button>
      {expanded && (
        docLayout === 'list' ? (
          <div className="px-3 pb-3 pt-1 bg-muted/20">
            <DocumentRowList
              documents={documents}
              onDownload={onDownload}
              onDelete={onDelete}
              onEdit={onEdit}
              onPreview={onPreview}
              onOpenDetail={onOpenDetail}
            />
          </div>
        ) : (
          <div className="px-3 pb-3 pt-1 bg-muted/20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {documents.map((doc) => (
              <DocumentTile
                key={doc.id}
                doc={doc}
                onDownload={onDownload}
                onDelete={onDelete}
                onEdit={onEdit}
                onPreview={onPreview}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

interface DocumentTileProps {
  doc: Document;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
}

export function DocumentTile({ doc, onDownload, onDelete, onEdit, onPreview, onOpenDetail }: DocumentTileProps) {
  const cleanDescription = stripImportMetadata(doc.description);
  // CR-5330: Tile body opens the details popup; the kebab menu still
  // exposes quick actions. stopPropagation() on the menu trigger keeps
  // the row click from firing alongside a menu item click.
  const handleBodyClick = () => onOpenDetail?.(doc);
  const handleBodyKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail?.(doc);
    }
  };
  return (
    <Card className="group hover:shadow-md transition-shadow h-full">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className={`flex items-start gap-3 flex-1 min-w-0 ${onOpenDetail ? 'cursor-pointer' : ''}`}
            onClick={onOpenDetail ? handleBodyClick : undefined}
            onKeyDown={onOpenDetail ? handleBodyKey : undefined}
            role={onOpenDetail ? 'button' : undefined}
            tabIndex={onOpenDetail ? 0 : undefined}
          >
            <div className="text-muted-foreground mt-0.5 shrink-0">{getFileIcon(doc)}</div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[13px] leading-snug line-clamp-2 break-words" title={doc.title}>
                {doc.title}
              </h3>
              {cleanDescription && (
                <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                  {cleanDescription}
                </p>
              )}
            </div>
          </div>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -mt-1 opacity-60 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDownload(doc)}>
                <Download className="h-4 w-4 mr-2" />Download
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPreview?.(doc)}>
                <Eye className="h-4 w-4 mr-2" />Preview
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(doc)}>
                  <Edit className="h-4 w-4 mr-2" />Edit
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem className="text-red-600" onClick={() => onDelete(doc)}>
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-3 border-t gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <User className="h-3 w-3 shrink-0" />
            <span className="truncate">{doc.uploaded_by_name || '—'}</span>
          </div>
          {doc.formatted_file_size && (
            <span className="shrink-0 tabular-nums">{doc.formatted_file_size}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 mt-2">
          {doc.is_public && (
            <Badge variant="outline" className="text-[10px]">Public</Badge>
          )}
          {/* CR-5329 */}
          {doc.requires_acceptance && (
            <Badge variant="default" className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">
              Requires acceptance
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function stripImportMetadata(description?: string): string {
  if (!description) return '';
  const clean = description
    .replace(/\[mab-doc#[^\]]+\]/gi, '')
    .replace(/Original\s*FileName\s*:[^|]*?(?=SourceID|$)/i, '')
    .replace(/SourceID\s*:\s*\S+/i, '')
    .trim();
  return clean.length > 3 ? clean : '';
}
